import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Card, formatCalendarDate, HourTrackAxis, Icon, Note } from '@repo/design-system';
import type { HourTrackRange } from '@repo/design-system';
import type { AuthoredOpeningHoursDay, AuthoredOpeningHoursVersion } from '@repo/frontend-types';
import { visuallyHidden } from '@repo/ui';
import { focus, radius, semantic, space, typeRole } from '@repo/design-tokens';

import DayRow, { DAY_ROW_COLUMNS, DAY_ROW_COLUMNS_BESIDE_CARD } from './DayRow';
import {
  anyDayDiffers,
  cloneWeek,
  copyMondayToWeekdays,
  differSentence,
  setEveryDay24h,
  panelSub,
  specialName,
  standardName,
  specialPanelSub,
  toTrackRanges,
  wasLine,
  WEEKDAYS,
  type CalendarGroup,
  type Weekday,
} from './hoursModel';
import { weekRefusal } from './weekRules';

/**
 * **The one gate every change passes** (spec twin-details-page-hours-week, R6.4): a row's track,
 * times or switch, *Add range*, *Remove*, *Copy Monday to Weekdays*, *Set 24/7* and *Undo*. It
 * applies the day rules and the rule across days to the whole proposed week, then saves it — or
 * refuses, saying why, and saves nothing, so the row stays as it was.
 */
export const commitWeek = (
  days: AuthoredOpeningHoursDay[],
  save: (days: AuthoredOpeningHoursDay[]) => void,
  refuse: (sentence: string) => void,
): boolean => {
  const refusal = weekRefusal(days);
  if (refusal !== null) {
    refuse(refusal);
    return false;
  }
  save(days);
  return true;
};

export type BulkKind = 'copy-monday' | 'all-24h' | 'same-as-standard';

/** Why *Same as Standard* has nothing to do (spec twin-details-page-hours-special, R5.1). */
export const sameAsStandardReason = (
  calendar: AuthoredOpeningHoursVersion,
  comparison: AuthoredOpeningHoursVersion | null,
): string | null =>
  !comparison
    ? `No standard hours on ${formatCalendarDate(calendar.effectiveDateFrom)} to copy`
    : !anyDayDiffers(calendar, comparison)
      ? 'Every day is already the same as standard hours'
      : null;

export interface WeekPanelProps {
  calendar: AuthoredOpeningHoursVersion;
  group: CalendarGroup;
  /** The read's zone; undefined when the read carried none (an older middleware, R12). */
  timezone?: string | null;
  /** The launch line (R10.3), or null while the hours are being read. */
  launchLine: { text: string; held: boolean } | null;
  /** The error-tone note: a refusal, the server's sentence, or *Someone else changed…*. */
  notice: string | null;
  /**
   * A neutral note under it, or null: after *Copy another twin* across timezones, *Times are copied
   * as they are…* (spec twin-details-page-hours-start, R4.4.5).
   */
  info?: string | null;
  /** The editor's count of answers it did not ask for — each day row's drafts follow it. */
  revision: number;
  /** Save a whole week for this calendar. */
  onSave: (days: AuthoredOpeningHoursDay[]) => void;
  /** Show (and announce) why a change was not applied. */
  onRefused: (sentence: string) => void;
  /** A bulk change was saved; `before` is the week it replaced, `after` the week it made, for *Undo*. */
  onBulk: (
    kind: BulkKind,
    before: AuthoredOpeningHoursDay[],
    after: AuthoredOpeningHoursDay[],
  ) => void;
  /** The *Remove* control for an upcoming week, or nothing. */
  removeSlot?: React.ReactNode;
  /**
   * A special period's week (spec twin-details-page-hours-special, R5): titled by its name, each day
   * compared with `comparison` — the standard covering its first day, or null — and *Same as
   * Standard* the only header action. A special is told by `calendar.kind`.
   */
  comparison?: AuthoredOpeningHoursVersion | null;
  /** R1.6's footer line, when a standard changes inside the period. */
  changeLine?: string | null;
  /** A special period not yet saved: the week it will start with, every row read-only (R5.5). */
  preview?: boolean;
  /** The special period card is docked beside the week, which narrows it (R6.8). */
  besideCard?: boolean;
  titleRef?: React.Ref<HTMLHeadingElement>;
  className?: string;
}

const WeekPanel: React.FC<WeekPanelProps> = ({
  calendar,
  group,
  timezone,
  launchLine,
  notice,
  info = null,
  revision,
  onSave,
  onRefused,
  onBulk,
  removeSlot,
  comparison = null,
  changeLine = null,
  preview = false,
  besideCard = false,
  titleRef,
  className,
}) => {
  const special = calendar.kind === 'special';
  // Nothing is editable that could be lost: a period's days open the moment it is saved (R5.5).
  const readOnly = group === 'past' || preview;
  const sameReasonId = React.useId();
  const [selectedDay, setSelectedDay] = useState<Weekday>('monday');
  /** Each day's ranges from before it was switched to 24 hours or Closed, in this visit. */
  const remembered = useRef(new Map<Weekday, HourTrackRange[]>());

  const days = WEEKDAYS.map(
    w =>
      calendar.days.find(d => d.dayOfWeek === w) ?? {
        dayOfWeek: w,
        state: 'closed' as const,
        ranges: [],
      },
  );

  const commit = (next: AuthoredOpeningHoursDay[]) => commitWeek(next, onSave, onRefused);

  const commitDay = (next: AuthoredOpeningHoursDay) => {
    const before = days.find(d => d.dayOfWeek === next.dayOfWeek);
    const applied = commit(days.map(d => (d.dayOfWeek === next.dayOfWeek ? next : d)));
    if (applied && before?.state === 'ranges' && next.state !== 'ranges') {
      remembered.current.set(before.dayOfWeek, toTrackRanges(before));
    }
  };

  const bulk = (bulkKind: BulkKind) => {
    const before = days;
    const next =
      bulkKind === 'copy-monday'
        ? copyMondayToWeekdays(days)
        : bulkKind === 'all-24h'
          ? setEveryDay24h(days)
          : cloneWeek((comparison as AuthoredOpeningHoursVersion).days);
    if (commit(next)) onBulk(bulkKind, before, next);
  };

  const sameReason = special && !preview ? sameAsStandardReason(calendar, comparison) : null;
  const wasFor = (d: AuthoredOpeningHoursDay) =>
    special && !preview && comparison
      ? wasLine(
          comparison.days.find(c => c.dayOfWeek === d.dayOfWeek),
          d,
        )
      : null;

  const title = preview
    ? 'New special period'
    : special
      ? specialName(calendar)
      : standardName(calendar);
  const sub = preview
    ? 'Name it and choose its dates'
    : special
      ? specialPanelSub(calendar)
      : panelSub(calendar, group, timezone);

  return (
    <Card
      surface="on-scene"
      size="lg"
      className={besideCard ? `${className ?? ''} beside-card` : className}
    >
      <section aria-labelledby="opening-hours-week-title">
        <header className="head">
          <div className="titles">
            <h3 id="opening-hours-week-title" ref={titleRef} tabIndex={-1}>
              {title}
            </h3>
            <p className="sub">{sub}</p>
          </div>
          {special && !readOnly && (
            <div className="actions">
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<Icon name="rotate-left" />}
                aria-disabled={sameReason !== null || undefined}
                aria-describedby={sameReason !== null ? sameReasonId : undefined}
                title={sameReason ?? undefined}
                onClick={() => {
                  if (sameReason === null) bulk('same-as-standard');
                }}
              >
                Same as Standard
              </Button>
              {sameReason !== null && (
                <span id={sameReasonId} style={visuallyHidden}>
                  {sameReason}
                </span>
              )}
            </div>
          )}
          {!special && !readOnly && (
            <div className="actions">
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<Icon name="copy" />}
                onClick={() => bulk('copy-monday')}
              >
                Copy Monday to Weekdays
              </Button>
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<Icon name="infinity" />}
                onClick={() => bulk('all-24h')}
              >
                Set 24/7
              </Button>
              {removeSlot}
            </div>
          )}
        </header>

        {notice && (
          <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />} className="notice">
            {notice}
          </Note>
        )}
        {info && (
          <Note tone="neutral" icon={<Icon name="circle-info" size="xs" />} className="notice">
            {info}
          </Note>
        )}

        <div className="axis-row" aria-hidden="true">
          <span />
          <span />
          <HourTrackAxis />
          <span />
        </div>
        <div className="rows">
          {days.map(d => (
            <DayRow
              key={d.dayOfWeek}
              day={d}
              selected={selectedDay === d.dayOfWeek}
              readOnly={readOnly}
              rememberedRanges={remembered.current.get(d.dayOfWeek) ?? null}
              onSelect={() => setSelectedDay(d.dayOfWeek)}
              onCommit={commitDay}
              revision={revision}
              onRefused={onRefused}
              was={wasFor(d)}
            />
          ))}
        </div>

        {special ? (
          <footer className="foot">
            {preview ? (
              <Note tone="neutral" bare icon={<Icon name="circle-info" size="xs" />}>
                Starts as a copy of standard hours.
              </Note>
            ) : group === 'past' ? (
              <Note tone="neutral" bare icon={<Icon name="box-archive" size="xs" />}>
                This special period has ended. Past hours are kept as they were.
              </Note>
            ) : (
              <>
                <Note tone="neutral" bare icon={<Icon name="calendar-days" size="xs" />}>
                  {differSentence(calendar, comparison)}
                </Note>
                {changeLine && (
                  <Note tone="neutral" bare icon={<Icon name="circle-info" size="xs" />}>
                    {changeLine}
                  </Note>
                )}
              </>
            )}
          </footer>
        ) : (
          <footer className="foot">
            {readOnly ? (
              <Note tone="neutral" bare icon={<Icon name="box-archive" size="xs" />}>
                These hours have ended. Past hours are kept as they were.
              </Note>
            ) : launchLine ? (
              <Note
                tone={launchLine.held ? 'error' : 'neutral'}
                bare
                icon={<Icon name={launchLine.held ? 'circle-exclamation' : 'check'} size="xs" />}
              >
                {launchLine.text}
              </Note>
            ) : null}
            <Note tone="neutral" bare icon={<Icon name="circle-info" size="xs" />}>
              Hours are times of day. A close earlier than the open runs into the next day.
              {group === 'now'
                ? ` Changes apply from ${formatCalendarDate(
                    calendar.effectiveDateFrom,
                  )}. If the hours change from a later date, use New Standard Hours From a Date.`
                : ''}
            </Note>
          </footer>
        )}
      </section>
    </Card>
  );
};

/*
 * The week panel is the design system's glass card (Card on-scene, lg, whose radius is radius.xl);
 * its region and name stay on the section inside. Frame 06's padding is held on the card.
 */
export default styled(WeekPanel)`
  min-width: 0;
  padding: ${space[4]} ${space[5]};

  > section {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${space[3]};
    min-width: 0;
  }

  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${space[4]};
  }

  h3 {
    margin: 0;
    ${typeRole.subtitle2};
    color: ${semantic.text.primary};
  }

  /* Focus is moved here after Add and Remove (R7.6, R7.7): no ring for that, the ring for a keyboard. */
  h3:focus {
    outline: none;
  }

  h3:focus-visible {
    border-radius: ${radius.sm};
    box-shadow: ${focus.tight};
  }

  .titles > .sub {
    margin: ${space[1]} 0 0;
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: ${space[2]};
  }

  .axis-row {
    display: grid;
    grid-template-columns: ${DAY_ROW_COLUMNS};
    gap: ${space[3]};
    padding: 0 ${space[3]};
  }

  &.beside-card .axis-row,
  &.beside-card .day-row {
    grid-template-columns: ${DAY_ROW_COLUMNS_BESIDE_CARD};
  }

  .rows {
    display: flex;
    flex-direction: column;
    /* Frame 06: rows 6 px apart (space[1] is 4, space[2] 8 — the frame's own step, held here). */
    gap: 6px;
  }

  .foot {
    display: flex;
    flex-wrap: wrap;
    gap: ${space[2]} ${space[5]};
    margin-top: auto;
    padding-top: ${space[3]};
    border-top: 1px solid ${semantic.border.faint};
  }
`;
